import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import OpalPresentationTable, {
  usePresentationTableExpandState
} from '../../../../organisms/DenaliAnalysis/tables/presentation'
import SydActionMenu from '../../../../commonDesign/SydActionMenu'
import SydTabs from '../../../../commonDesign/SydTabs'
import ReportStatusBadge from '../components/ReportStatusBadge'
import { useFormattingContext } from '../../../../organisms/FormattingProvider/FormattingContext'
import Skeleton from '../../../../atoms/Skeleton'
import { useGetActiveClientStatusReport } from '../../../../../api/groups'
import { useCheckPolicy } from '../../../../../hooks'
import FinalizeReportModal from '../components/StatusReportModals/FinalizeReportModal'
import StatusReportModal from '../components/StatusReportModals'
import StatusReportHistoryTab from './StatusReportHistoryTab'
import StatusReportEmpty from './StatusReportEmpty'
import { getColumns } from './utils/columnConfig'

const tabOptions = {
  active: { label: 'Active Status Report', order: 1 },
  previous: { label: 'Previous Status Reports', order: 2 }
}

function StatusReportAssignmentDetails () {
  const { formatter } = useFormattingContext()
  const { statusTemplateId, levelId } = useParams()

  const [showModal, setShowModal] = useState(null)

  const canEditMilestones = useCheckPolicy('editMilestones')

  const { data, isLoading, isFetching } = useGetActiveClientStatusReport({
    filters: {
      statusTemplateId,
      levelId
    },
    includes: {
      items: true
    }
  })

  const statusReport = useMemo(() => data?.statusReport, [data])

  const { expandState, setExpandState } = usePresentationTableExpandState(true)

  const actions = useMemo(() => {
    if (!statusReport?.statusReportId) return []
    return [
      {
        key: 'finalize-report-status',
        label: 'Finalize This Report',
        icon: 'layer',
        onClick: () => setShowModal({ statusReportId: statusReport.statusReportId, mode: 'finalize' }),
        disabled: statusReport.status !== 'active'
      },
      {
        key: 'run-new-report',
        label: 'Run New Report',
        icon: 'refresh',
        onClick: () => setShowModal({ levelId, statusTemplateId, mode: 'run-new' }),
        disabled: statusReport.status !== 'archived'
      },
      {
        key: 'archive-report-status',
        label: 'Archive This Report',
        icon: 'lock',
        onClick: () => setShowModal({ statusReportId: statusReport.statusReportId, mode: 'archive' }),
        disabled: statusReport.status === 'archived'
      },
      {
        key: 'summary',
        label: 'Get Summary',
        icon: 'document',
        onClick: () => setShowModal({ statusReportId: statusReport.statusReportId, mode: 'summary' }),
        disabled: false
      },
      {
        key: 'create-wealth-journey',
        label: 'Create Wealth Journey Entry',
        icon: 'calendar',
        onClick: () => setShowModal({ statusReportId: statusReport.statusReportId, mode: 'wealth-journey-entry' }),
        disabled: false
      }
    ].filter((a) => !a.disabled)
  }, [levelId, statusReport, statusTemplateId])

  const columns = useMemo(() => getColumns({ statuses: data?.statuses }), [data])

  if (isLoading || isFetching) {
    return (
      <div className='w-full flex flex-col gap-5 p-5 px-15'>
        <div className='w-full flex flex-col gap-2'>
          <Skeleton height='26px' width='400px' />
          <Skeleton height='20px' width='200px' />
        </div>
        <Skeleton height='400px' width='100%' />
      </div>
    )
  }

  return (
    <>
      {!statusReport ? (
        <StatusReportEmpty
          onTriggerNewReport={() => {
            setShowModal({ mode: 'run-new', levelId, statusTemplateId })
          }}
        />
      ) : (
        <div className='w-full flex flex-col gap-5 p-5 px-15'>
          <div className='flex justify-between items-center gap-4'>
            <div>
              <div className='font-size-xl font-weight-bold'>
                {statusReport?.statusTemplateName} for {statusReport?.levelName}
              </div>
              <div className='flex gap-2 items-center'>
                <ReportStatusBadge codeName={statusReport?.status} />
                <span className='font-size-xs'>As of {formatter(statusReport?.asOfDate, '@MM/DD/YY')}</span>
              </div>
            </div>
            <div>
              <SydActionMenu subject={statusReport} actions={actions} />
            </div>
          </div>

          <SydTabs.Group tabOptions={tabOptions} defaultValue='active'>
            <SydTabs.List />

            <SydTabs.Tab value='active'>
              <OpalPresentationTable
                isLoading={isLoading}
                data={statusReport?.items ?? []}
                columns={columns}
                expandState={expandState}
                onExpandStateChange={setExpandState}
                getSubRows={(row) => row?.items}
                className='zebra-rows'
              />
            </SydTabs.Tab>

            <SydTabs.Tab value='previous'>
              <StatusReportHistoryTab />
            </SydTabs.Tab>
          </SydTabs.Group>

          {showModal?.statusReportId && showModal?.mode === 'finalize' && (
            <FinalizeReportModal
              open
              onClose={() => setShowModal(null)}
              levelId={levelId}
              statusTemplateId={statusTemplateId}
              onSuccess={(data) => {
                if (canEditMilestones) {
                  setShowModal({
                    statusReportId: data.statusReportId,
                    mode: 'wealth-journey-entry'
                  })
                } else {
                  setShowModal(null)
                }
              }}
            />
          )}
        </div>
      )}

      <StatusReportModal
        onClose={() => setShowModal(null)}
        {...showModal}
      />
    </>
  )
}

export default StatusReportAssignmentDetails
